import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ErroRede } from '../api.js'
import { enfileirar, guardarCache, lerCache } from '../offline.js'
import { useSessao } from '../sessao.jsx'
import Layout from '../componentes/Layout.jsx'
import {
  Campo, Aviso, Erro, EstadoVazio, Carregando, emKm, emNumero, emReais, emKwh, emData,
  NOME_ENERGIA, unidadeDaEnergia, sobOMedidor,
} from '../componentes/basicos.jsx'

/**
 * A tela mais usada do sistema (SPEC §7): meta de lançamento completo em menos de
 * 20 segundos, no celular, em pé na bomba. Tudo que dá para inferir vem preenchido;
 * o que sobra são três toques e dois números.
 */
export default function Abastecer() {
  const { usuario, recarregar } = useSessao()
  const [base, setBase] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [erroBase, setErroBase] = useState(null)

  const [veiculoId, setVeiculoId] = useState(null)
  const [energia, setEnergia] = useState(null)
  const [quantidade, setQuantidade] = useState('')
  const [leitura, setLeitura] = useState('')
  const [custo, setCusto] = useState('')
  const [odometro, setOdometro] = useState('')
  const [pontoId, setPontoId] = useState(null)
  const [tanqueCompleto, setTanqueCompleto] = useState(null)
  const [socInicial, setSocInicial] = useState('')
  const [socFinal, setSocFinal] = useState('')
  const [obs, setObs] = useState('')
  const [quando, setQuando] = useState('')
  const [abertura, setAbertura] = useState(false)

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState(null)
  const [resultado, setResultado] = useState(null)

  // Carrega do servidor e guarda em cache; sem rede, usa o que ficou guardado.
  useEffect(() => {
    let vivo = true
    ;(async () => {
      try {
        const [{ veiculos }, { pontos }, painel] = await Promise.all([
          api.get('/veiculos'), api.get('/pontos'), api.get('/dashboard'),
        ])
        const odometros = Object.fromEntries(
          painel.veiculos.map((c) => [c.veiculo.id, c.odometro_atual]),
        )
        // Quantos lançamentos cada veículo já tem: decide se a abertura ainda
        // faz sentido (ela precisa ser a primeira, e há uma por veículo).
        const contagens = Object.fromEntries(
          painel.veiculos.map((c) => [c.veiculo.id, c.lancamentos]),
        )
        if (vivo) setBase({ veiculos, pontos, odometros, contagens })
      } catch (e) {
        const guardado = await lerCache(usuario.id, 'formulario').catch(() => null)
        if (!vivo) return
        if (guardado) setBase(guardado)
        else setErroBase(e)
      } finally {
        if (vivo) setCarregando(false)
      }
    })()
    return () => { vivo = false }
  }, [usuario.id])

  // Todo estado do formulário vai para o cache — inclusive o que muda a cada
  // lançamento (odômetro, leitura do relógio). Sem isso, reabrir o app sem rede
  // voltava ao odômetro de antes dos lançamentos que ficaram na fila.
  useEffect(() => {
    if (base) guardarCache(usuario.id, 'formulario', base).catch(() => {})
  }, [base, usuario.id])

  const veiculos = base?.veiculos ?? []
  const veiculo = veiculos.find((v) => v.id === veiculoId) ?? veiculos[0] ?? null
  const pontos = base?.pontos ?? []

  // Preenche o que dá para inferir assim que o veículo é conhecido.
  useEffect(() => {
    if (!veiculo) return
    setVeiculoId(veiculo.id)
    setEnergia((atual) => (veiculo.energias_aceitas.includes(atual) ? atual : veiculo.energias_aceitas[0]))
    setOdometro(String(base?.odometros?.[veiculo.id] ?? veiculo.odometro_inicial ?? ''))
    setTanqueCompleto(null)
    if ((base?.contagens?.[veiculo.id] ?? 0) > 0) setAbertura(false)
  }, [veiculo?.id, base]) // eslint-disable-line react-hooks/exhaustive-deps

  const eLiquida = energia && energia !== 'eletricidade'
  const odometroMinimo = base?.odometros?.[veiculo?.id] ?? veiculo?.odometro_inicial ?? 0
  const primeiroLancamento = (base?.contagens?.[veiculo?.id] ?? 0) === 0

  const pontosSugeridos = useMemo(() => {
    // Ponto residencial só faz sentido para eletricidade; posto, para combustível.
    if (!energia) return pontos
    return pontos.filter((p) => (
      energia === 'eletricidade' ? p.tipo !== 'posto' : p.tipo === 'posto'
    ))
  }, [pontos, energia])

  // Havendo um único ponto compatível, ele já vem marcado. Era o passo mais fácil
  // de esquecer — e sem ponto não há tarifa, então a recarga saía com custo zero.
  useEffect(() => {
    if (pontosSugeridos.length === 1) setPontoId(pontosSugeridos[0].id)
    else if (pontoId && !pontosSugeridos.some((p) => p.id === pontoId)) setPontoId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pontosSugeridos])

  // Ponto com relógio próprio (SPEC §5.8): o campo pede a leitura em vez dos kWh.
  const ponto = pontos.find((p) => p.id === pontoId) ?? null
  const medido = sobOMedidor(ponto, {
    energia,
    abertura,
    inicio: quando ? new Date(quando).toISOString() : new Date().toISOString(),
  })

  // Prévia para conferir antes de salvar. Quem decide é o servidor: com uma
  // recarga retroativa, a leitura anterior pode não ser a última.
  const kwhPelaLeitura = useMemo(() => {
    if (!medido) return null
    const l = Number(String(leitura).replace(',', '.'))
    const diferenca = l - ponto.medidor.ultima_leitura
    return l > 0 && diferenca > 0 ? Number(diferenca.toFixed(3)) : null
  }, [medido, leitura, ponto])

  const precoDerivado = useMemo(() => {
    const q = medido ? kwhPelaLeitura : Number(String(quantidade).replace(',', '.'))
    const c = Number(String(custo).replace(',', '.'))
    if (!(q > 0) || !(c > 0)) return null
    return c / q
  }, [medido, kwhPelaLeitura, quantidade, custo])

  function limpar() {
    setQuantidade('')
    setLeitura('')
    setCusto('')
    setSocInicial('')
    setSocFinal('')
    setObs('')
    setQuando('')
    setTanqueCompleto(null)
    setAbertura(false)
  }

  async function enviar(evento) {
    evento.preventDefault()
    setErro(null)
    setResultado(null)

    const numero = (texto) => {
      const v = Number(String(texto).replace(',', '.'))
      return Number.isFinite(v) ? v : null
    }

    const lancamento = {
      veiculo_id: veiculo.id,
      ponto_id: pontoId,
      energia,
      ...(medido ? { medidor_final: numero(leitura) } : { quantidade: numero(quantidade) }),
      odometro: numero(odometro),
      inicio: quando ? new Date(quando).toISOString() : new Date().toISOString(),
      ...(custo !== '' ? { custo: numero(custo) } : {}),
      ...(eLiquida ? { tanque_completo: tanqueCompleto === true } : {}),
      ...(socInicial !== '' ? { soc_inicial: numero(socInicial) } : {}),
      ...(socFinal !== '' ? { soc_final: numero(socFinal) } : {}),
      ...(obs ? { obs } : {}),
      ...(abertura ? { abertura: true } : {}),
      idempotency_key: crypto.randomUUID(),
    }

    if (eLiquida && tanqueCompleto === null) {
      setErro(new Error('Escolha "Completei" ou "Parcial".'))
      return
    }

    // O que o próximo lançamento precisa saber deste, com ou sem rede: odômetro,
    // contagem do veículo e — no ponto com relógio — a leitura, que vale para
    // qualquer veículo que carregar ali depois.
    const registrarNaBase = () => setBase((b) => ({
      ...b,
      odometros: { ...b.odometros, [veiculo.id]: lancamento.odometro },
      contagens: { ...b.contagens, [veiculo.id]: (b.contagens?.[veiculo.id] ?? 0) + 1 },
      pontos: b.pontos.map((p) => (
        medido && p.id === ponto.id && lancamento.inicio >= p.medidor.ultima_leitura_em
          ? { ...p, medidor: { ...p.medidor, ultima_leitura: lancamento.medidor_final, ultima_leitura_em: lancamento.inicio } }
          : p
      )),
    }))

    setEnviando(true)
    try {
      const r = await api.post('/abastecimentos', lancamento)
      setResultado({ tipo: 'enviado', ...r })
      limpar()
      setOdometro(String(lancamento.odometro))
      registrarNaBase()
    } catch (e) {
      // Sem conexão ou com a sessão vencida, o lançamento vai para a fila com a
      // chave já gerada — o servidor descarta reenvios duplicados na sincronização.
      // Sessão vencida não pode custar o que foi digitado: para entrar de novo a
      // pessoa sairia da tela, e o formulário iria junto.
      const sessaoVencida = e.status === 401
      if (e instanceof ErroRede || sessaoVencida) {
        await enfileirar(lancamento, usuario.id)
        setResultado({ tipo: sessaoVencida ? 'sessao' : 'fila' })
        limpar()
        registrarNaBase()
      } else {
        setErro(e)
      }
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) return <Layout titulo="Abastecer"><Carregando /></Layout>

  if (erroBase) {
    return <Layout titulo="Abastecer"><Erro erro={erroBase} /></Layout>
  }

  if (veiculos.length === 0) {
    return (
      <Layout titulo="Abastecer">
        <EstadoVazio titulo="Nenhum veículo cadastrado">
          <Link className="botao botao-principal" to="/garagem">Cadastrar veículo</Link>
        </EstadoVazio>
      </Layout>
    )
  }

  return (
    <Layout titulo="Abastecer">
      {/* Com um veículo só, o seletor some (SPEC §7). */}
      {veiculos.length > 1 && (
        <div className="chips" role="group" aria-label="Veículo">
          {veiculos.map((v) => (
            <button key={v.id} type="button" className="chip"
                    aria-pressed={v.id === veiculo.id}
                    onClick={() => setVeiculoId(v.id)}>
              {v.nome}
            </button>
          ))}
        </div>
      )}

      <form className="cartao pilha" onSubmit={enviar}>
        {veiculo.energias_aceitas.length > 1 && (
          <div className="campo">
            <label>Energia</label>
            <div className="chips" role="group" aria-label="Energia">
              {veiculo.energias_aceitas.map((e) => (
                <button key={e} type="button" className="chip" aria-pressed={e === energia}
                        onClick={() => setEnergia(e)}>
                  {NOME_ENERGIA[e]}
                </button>
              ))}
            </div>
          </div>
        )}

        {medido ? (
          <Campo
            rotulo="Relógio ao final (kWh)"
            dica={
              kwhPelaLeitura
                ? `${emKwh(kwhPelaLeitura)} nesta recarga (última leitura: ${emKwh(ponto.medidor.ultima_leitura)})`
                : `Última leitura: ${emKwh(ponto.medidor.ultima_leitura)}, em ${emData(ponto.medidor.ultima_leitura_em)}. `
                  + 'Esqueceu de anotar uma recarga? Lance só a próxima: a leitura dela cobre as duas.'
            }
          >
            <input inputMode="decimal" value={leitura} required autoFocus
                   placeholder="em quanto o relógio parou"
                   onChange={(e) => setLeitura(e.target.value)} />
          </Campo>
        ) : (
          <Campo rotulo={`Quantidade (${unidadeDaEnergia(energia)})`}>
            <input inputMode="decimal" value={quantidade} required autoFocus
                   placeholder={energia === 'eletricidade' ? 'kWh carregados' : 'litros na bomba'}
                   onChange={(e) => setQuantidade(e.target.value)} />
          </Campo>
        )}

        <Campo
          rotulo="Valor total pago (R$)"
          dica={
            precoDerivado
              ? `Preço por ${unidadeDaEnergia(energia).toLowerCase()}: ${emReais(precoDerivado)}`
              : (!eLiquida ? 'Deixe vazio para calcular pela tarifa da conta de luz.' : undefined)
          }
        >
          <input inputMode="decimal" value={custo} required={eLiquida}
                 placeholder={eLiquida ? 'o que apareceu na bomba' : 'opcional'}
                 onChange={(e) => setCusto(e.target.value)} />
        </Campo>

        <Campo rotulo="Odômetro (km)" dica={`Último registrado: ${emKm(odometroMinimo)}`}>
          <input inputMode="numeric" value={odometro} required
                 onChange={(e) => setOdometro(e.target.value)} />
        </Campo>

        {eLiquida && (
          <div className="campo">
            <label>Tanque</label>
            <div className="par-botoes">
              <button type="button" aria-pressed={tanqueCompleto === true}
                      onClick={() => setTanqueCompleto(true)}>Completei</button>
              <button type="button" aria-pressed={tanqueCompleto === false}
                      onClick={() => setTanqueCompleto(false)}>Parcial</button>
            </div>
            <span className="dica">O consumo tanque-a-tanque depende desta escolha.</span>
          </div>
        )}

        {pontosSugeridos.length > 0 && (
          <div className="campo">
            <label>Onde</label>
            <div className="chips" role="group" aria-label="Ponto de abastecimento">
              {pontosSugeridos.map((p) => (
                <button key={p.id} type="button" className="chip" aria-pressed={p.id === pontoId}
                        onClick={() => setPontoId(p.id === pontoId ? null : p.id)}>
                  {p.nome}
                </button>
              ))}
            </div>
          </div>
        )}

        <Campo rotulo="Data e hora" dica="Vazio = agora.">
          <input type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} />
        </Campo>

        {energia === 'eletricidade' && (
          <div className="linha">
            <div className="crescer">
              <Campo rotulo="Bateria inicial (%)">
                <input inputMode="numeric" value={socInicial}
                       onChange={(e) => setSocInicial(e.target.value)} />
              </Campo>
            </div>
            <div className="crescer">
              <Campo rotulo="Bateria final (%)">
                <input inputMode="numeric" value={socFinal}
                       onChange={(e) => setSocFinal(e.target.value)} />
              </Campo>
            </div>
          </div>
        )}

        <Campo rotulo="Observação">
          <input value={obs} onChange={(e) => setObs(e.target.value)} />
        </Campo>

        {/* Só aparece quando o veículo ainda não tem nenhum lançamento: a abertura
            precisa ser a primeira e existe uma por veículo. Depois disso o campo
            seria uma escolha que o servidor recusaria de qualquer jeito. */}
        {primeiroLancamento && (
          <label className="linha" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <input type="checkbox" style={{ width: 20, minHeight: 20, marginTop: 2, flexShrink: 0 }}
                   checked={abertura} onChange={(e) => setAbertura(e.target.checked)} />
            <span>
              <strong>Lançamento de abertura</strong>
              <span className="dica" style={{ display: 'block' }}>
                Marque se esta energia já foi consumida até o odômetro informado —
                o acumulado de antes de você começar a registrar. O consumo é medido
                do odômetro inicial do veículo até aqui.
              </span>
            </span>
          </label>
        )}

        <Erro erro={erro} />

        <button type="submit" className="botao-principal botao-largo" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar lançamento'}
        </button>
      </form>

      {resultado?.tipo === 'fila' && (
        <Aviso tipo="ok">
          Salvo no aparelho. Vai para o servidor sozinho quando a conexão voltar.
        </Aviso>
      )}

      {resultado?.tipo === 'sessao' && (
        <Aviso tipo="atencao">
          Sua sessão expirou. O lançamento ficou guardado no aparelho e vai para o servidor
          assim que você entrar de novo.{' '}
          <button type="button" style={{ minHeight: 36, marginTop: 8 }} onClick={recarregar}>
            Entrar de novo
          </button>
        </Aviso>
      )}

      {resultado?.tipo === 'enviado' && <ResumoDoTrecho resultado={resultado} />}
    </Layout>
  )
}

/** Confirmação com as métricas do trecho — é o retorno que pega erro de digitação. */
function ResumoDoTrecho({ resultado }) {
  const { trecho, abastecimento, aviso } = resultado

  return (
    <div className="cartao pilha">
      <Aviso tipo="ok">Lançamento registrado.</Aviso>

      {aviso && <Aviso tipo="atencao">{aviso}</Aviso>}

      {/* A conta do relógio às claras: é aqui que um dígito trocado aparece. */}
      {abastecimento.medidor_final !== null && (
        <p className="dica">
          Relógio de {emKwh(abastecimento.medidor_inicial)} a {emKwh(abastecimento.medidor_final)}:{' '}
          <strong>{emKwh(abastecimento.quantidade)}</strong> nesta recarga.
        </p>
      )}

      <div className="grade-cartoes">
        <div className="metrica">
          <span className="valor">{emKm(trecho?.km_desde_anterior)}</span>
          <span className="rotulo">desde o anterior</span>
        </div>
        <div className="metrica">
          <span className="valor">
            {trecho?.consumo ? `${emNumero(trecho.consumo)} ${trecho.unidade_consumo}` : '—'}
          </span>
          <span className="rotulo">
            {trecho?.metodo === 'tanque_a_tanque' ? 'consumo tanque-a-tanque' : 'consumo do trecho'}
          </span>
        </div>
        <div className="metrica">
          <span className="valor">{trecho?.custo_por_km ? emReais(trecho.custo_por_km) : '—'}</span>
          <span className="rotulo">por km</span>
        </div>
        <div className="metrica">
          <span className="valor">{emReais(abastecimento.custo)}</span>
          <span className="rotulo">custo do lançamento</span>
        </div>
      </div>

      {trecho?.soc_consumido !== null && trecho?.soc_consumido !== undefined && (
        <p className="dica">
          O percurso até aqui consumiu <strong>{trecho.soc_consumido}%</strong> da bateria.
        </p>
      )}

      {!trecho?.consumo && trecho?.metodo === null && (
        <p className="dica">
          O consumo aparece quando houver um trecho fechado — no combustível, entre dois
          tanques completos.
        </p>
      )}
    </div>
  )
}
